import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Button from '../../../../../components/ui/Button/Button';
import Input from '../../../../../components/ui/Input/Input';
import Switch from '../../../../../components/ui/Switch/Switch';
import {
  getDelegatedSettings, referenceFields, saveDelegatedSettings, SettingsDocument, SettingsValues,
} from '../../../../../crud/management_api/settings_delegation';
import { settingsMessages as copy } from '../../../../../configuration/settingsDelegation';

interface Props { localId: number; canEdit: boolean }

/** Dedicated settings-only view; never imports or mounts permission administration. */
export default function DelegatedSettings({ localId, canEdit }: Props) {
  const [document, setDocument] = useState<SettingsDocument | null>(null);
  const [draft, setDraft] = useState<SettingsValues | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const controller = useRef<AbortController>();
  useEffect(() => {
    const request = new AbortController();
    controller.current = request;
    setDocument(null);
    setDraft(null);
    setError('');
    setSaving(false);
    getDelegatedSettings(localId, request.signal).then(data => {
      if (!request.signal.aborted) { setDocument(data); setDraft(data.values); }
    }).catch(() => {
      if (!request.signal.aborted) setError(copy.unavailable);
    });
    return () => { request.abort(); controller.current?.abort(); };
  }, [localId, reload, canEdit]);

  const editable = canEdit && document?.can_edit === true && !saving && !error;
  const validTip = draft?.default_tip === null
    ? document?.values.default_tip === null
    : typeof draft?.default_tip === 'string'
      && /^(0|[1-9]\d{0,2})(\.\d{1,2})?$/.test(draft.default_tip)
      && Number(draft.default_tip) <= 100;
  const save = async () => {
    if (!document || !draft || !editable || !validTip) return;
    const request = new AbortController();
    controller.current = request;
    setSaving(true);
    try {
      const values = await saveDelegatedSettings(localId, document.values, draft, request.signal);
      if (!request.signal.aborted) {
        setDocument({ ...document, values });
        setDraft(values);
        toast.success(copy.saved);
      }
    } catch (error) {
      if (!request.signal.aborted) {
        setError(axios.isAxiosError(error) && error.response?.status === 409 ? copy.conflict : copy.unavailable);
      }
    } finally {
      if (!request.signal.aborted) setSaving(false);
    }
  };
  return (
    <section aria-label={copy.title}>
      <h2>{copy.title}</h2>
      <p>{copy.scope}</p>
      {error && <>
        <p role="alert">{error}</p>
        <Button onClick={() => { setSaving(false); setReload(value => value + 1); }}>{copy.reload}</Button>
      </>}
      {!document && !error && <p role="status">{copy.loading}</p>}
      {document && draft && <>
        {(!canEdit || !document.can_edit) && <p>{copy.readOnly}</p>}
        {referenceFields.map(field => (
          <Form.Group key={field} controlId={`feature-rollout-${field}`}>
            <Form.Label>{copy[field]}</Form.Label>
            <Form.Select disabled={!editable} value={draft[field] ?? ''}
              onChange={event => setDraft({ ...draft, [field]: event.target.value ? Number(event.target.value) : null })}>
              <option value="" disabled={document.values[field] !== null}>{copy.noValue}</option>
              {draft[field] !== null && !document.catalogs[field].some(row => row.id === draft[field])
                && <option value={draft[field] ?? ''}>{copy.unknown} ({draft[field]})</option>}
              {document.catalogs[field].map(row => <option key={row.id} value={row.id}>{row.label}</option>)}
            </Form.Select>
          </Form.Group>
        ))}
        {(['print_orders', 'local_open'] as const).map(field => (
          <div key={field}>
            <Switch id={`feature-rollout-${field}`} label={copy[field]} checked={draft[field] === true}
              disabled={!editable} onChange={value => setDraft({ ...draft, [field]: value })} />
            {draft[field] === null && <span>{copy.unknown}</span>}
          </div>
        ))}
        <Input id="settings-delegation-default-tip" label={copy.default_tip} inputMode="decimal"
          disabled={!editable} value={draft.default_tip ?? ''}
          onChange={event => setDraft({ ...draft, default_tip: event.target.value || null })} />
        {!validTip && <p role="alert">{copy.invalidTip}</p>}
        <Button onClick={save} disabled={!editable || !validTip} loading={saving}>{copy.save}</Button>
      </>}
    </section>
  );
}
